import { Box, styled } from '@mui/material'
import { IconContext } from '@phosphor-icons/react'
import { MouseEventHandler, ReactNode } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { tokens } from 'ComponentLibrary/style-dictionary/output'
import { TextWithEllipsis } from 'ComponentLibrary/src/piazza/text'

const APP_BAR_ICON_SIZE = tokens.SizingIconM
const OVERLAY_BADGE_SIZE = '20px'
const OVERLAY_ICON_SIZE = '16px'
const ICON_CONTAINER_SELECTOR = '[data-app-bar-icon-container]'
const AVATAR_ICON_SELECTOR = '[data-app-bar-avatar-icon]'

type BaseAppBarNavLinkProps = {
    label?: string
    startIcon?: ReactNode
    endIcon?: ReactNode
    overlay?: ReactNode
    caps?: boolean
    selected?: boolean
    forceReload?: boolean
}

type AppBarNavLinkWithHref = BaseAppBarNavLinkProps & {
    href: string
    onClick?: never
}

type AppBarNavLinkWithAction = BaseAppBarNavLinkProps & {
    href?: never
    onClick: MouseEventHandler<HTMLButtonElement>
}

export type AppBarNavLinkProps = AppBarNavLinkWithHref | AppBarNavLinkWithAction

export function AppBarNavLink({ label, startIcon, endIcon, overlay, caps = true, selected, forceReload, ...props }: AppBarNavLinkProps) {
    const content = (
        <IconContext.Provider value={{ size: APP_BAR_ICON_SIZE, color: 'currentColor' }}>
            {startIcon && (
                <IconContainer data-app-bar-icon-container>
                    {startIcon}
                    {overlay && (
                        <OverlayContainer>
                            <IconContext.Provider value={{ size: OVERLAY_ICON_SIZE, color: 'currentColor' }}>
                                {overlay}
                            </IconContext.Provider>
                        </OverlayContainer>
                    )}
                </IconContainer>
            )}
            {label && (
                <TextWithEllipsis
                    tooltipText={label}
                    placement="right"
                    typographyProps={{ variant: caps ? 'linkCaps' : 'link', color: 'inherit' }}
                >
                    {label}
                </TextWithEllipsis>
            )}
            {endIcon && <EndIconContainer data-app-bar-end-icon>{endIcon}</EndIconContainer>}
        </IconContext.Provider>
    )

    return 'onClick' in props ? (
        <StyledActionButton type="button" onClick={props.onClick} $selected={selected}>
            {content}
        </StyledActionButton>
    ) : (
        <StyledRouterLink $selected={selected} to={props.href} reloadDocument={forceReload}>
            {content}
        </StyledRouterLink>
    )
}

const appBarNavLinkStyles = ({ $selected }: { $selected?: boolean }) => ({
    display: 'inline-flex',
    gap: tokens.SizingSpacing075,
    alignItems: 'center',
    width: 'fit-content',
    minWidth: APP_BAR_ICON_SIZE,
    height: APP_BAR_ICON_SIZE,
    color: $selected ? tokens.ColorTextLinkSelected : tokens.ColorTextLinkDefault,
    textDecoration: 'none',
    cursor: 'pointer',
    [ICON_CONTAINER_SELECTOR]: {
        color: $selected ? tokens.ColorIconLinkSelected : tokens.ColorIconLinkDefault,
    },
    '&:hover': {
        color: tokens.ColorTextLinkHover,
        [ICON_CONTAINER_SELECTOR]: {
            color: tokens.ColorIconLinkHover,
        },
        [AVATAR_ICON_SELECTOR]: {
            outline: `${tokens.SizingBorderM} solid ${tokens.ColorIconLinkHover}`,
        },
    },
    '&:active': {
        color: tokens.ColorTextLinkPressed,
        [ICON_CONTAINER_SELECTOR]: {
            color: tokens.ColorIconLinkPressed,
        },
        [AVATAR_ICON_SELECTOR]: {
            outline: `${tokens.SizingBorderM} solid ${tokens.ColorIconLinkPressed}`,
        },
    },
})

const StyledRouterLink = styled(RouterLink, {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $selected?: boolean }>(({ $selected }) => appBarNavLinkStyles({ $selected }))

const StyledActionButton = styled('button', {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $selected?: boolean }>(({ $selected }) => ({
    ...appBarNavLinkStyles({ $selected }),
    padding: 0,
    border: 0,
    background: 'transparent',
    font: 'inherit',
}))

const IconContainer = styled(Box)(() => ({
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: APP_BAR_ICON_SIZE,
    height: APP_BAR_ICON_SIZE,
    flexShrink: 0,
}))

const EndIconContainer = styled('span')({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
})

const OverlayContainer = styled(Box)(() => ({
    position: 'absolute',
    right: '-8px',
    bottom: '-8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: OVERLAY_BADGE_SIZE,
    height: OVERLAY_BADGE_SIZE,
    borderRadius: '50%',
    backgroundColor: tokens.ColorSurfaceDefault,
    color: 'inherit',
    flexShrink: 0,
    '& > svg': {
        width: OVERLAY_ICON_SIZE,
        height: OVERLAY_ICON_SIZE,
    },
}))
